// Seaquake tremor and silt for the fast client (R101, re-shaped by the
// settlement work, ported by R499): during the Seaquake arc the camera
// shudders and silt shakes loose off the ridges.
//
// The clock is not copied. `render/tremor.ts` is the engine-free leaf that
// owns the bursts, the envelope and the camera offset, and both clients
// import it, so the two answers cannot drift. This file owns the silt only,
// which is `render/quake.ts:108-159` with the sprites turned into quads of
// the atlas `silt` cell.
//
// THE RULE THIS FILE EXISTS TO ENFORCE: **the layer stays visible**. An
// invisible layer skips `update` (`fast/layer.ts:133-138`, §10.3), and the
// tremor clock lives in `update`. A layer that stops stepping stops the
// quake, and a mid-quake re-enable then lands in a fresh burst rather than
// the live one. So the integrator never calls `setVisible(false)` here. The
// fluff switch rides the `emitSilt` argument of `feed` instead
// (`render/quake.ts:91-106`), and the `tremor` knob zeroes the offset
// without stopping the clock. Reduced motion does the same thing one level
// down: `Tremor` reads `reducedMotion()` in its constructor and drops the
// shudder while it keeps the silt (R184, §7).
//
// Positions for the silt come from the blocked mask (`BLOCK_RIDGE`), never
// from how ridges happen to be drawn — the terrain layer is free to re-treat
// them.

import { BLOCK_RIDGE, HEX_SIZE, cellCenterX, cellCenterY, mulberry32 } from '../../world';
// The clock itself, shared with the PixiJS client.
import { Tremor } from '../../render/tremor';
// --- invented figures, all of them; the values and their rationale ride
// data/presentation/presentation.toml [presentation.quake] (R101 and the
// settlement entries), generated into gen/presentation.ts.
import {
  QUAKE_SILT_ALPHA as SILT_ALPHA,
  QUAKE_SILT_COLOR as SILT_COLOR,
  QUAKE_SILT_DRIFT_FRAC as SILT_DRIFT_FRAC,
  QUAKE_SILT_FALL_PX_PER_SEC as SILT_FALL_PX_PER_SEC,
  QUAKE_SILT_FALL_SPAN_FRAC as SILT_FALL_SPAN_FRAC,
  QUAKE_SILT_LIFE_SEC as SILT_LIFE_SEC,
  QUAKE_SILT_MAX as SILT_MAX,
  QUAKE_SILT_SIZE_PX as SILT_SIZE_PX,
} from '../../gen/presentation';
import { outside, type DrawCtx, type FrameInfo, type Layer, type ViewRect } from '../layer';
import type { AtlasEntry } from '../gfx/atlas';
import type { BatchSource, QuadSink } from './batches';
import { cellsOf } from './shared';

/** How many floats one grain takes: x, y, vx, vy, age, scale. */
const GRAIN_STRIDE = 6;

export class QuakeLayer implements Layer {
  readonly name = 'quake';

  private readonly quads: QuadSink;
  private readonly cell: AtlasEntry;
  private shown = true;

  private readonly tremor = new Tremor();
  private ridges: Int32Array = new Int32Array(0);

  /** x, y, vx, vy, age, scale for each live grain, in one flat pool. */
  private pool: Float32Array;
  private grains = 0;
  private cap = SILT_MAX;
  private rand = mulberry32(0x5117);

  private stage = 0;
  private settleT: number | null = null;
  private emitSilt = true;

  private siltOn = true;
  private tremorOn = true;
  /** The grains a `shock` owes the next `update` (R1535). */
  private owedSilt = 0;

  constructor(src: BatchSource) {
    this.quads = src.newQuads(SILT_MAX);
    this.cell = src.sprite('silt');
    this.pool = new Float32Array(this.cap * GRAIN_STRIDE);
  }

  /** The camera shudder in CSS px, zero while the `tremor` knob is off. */
  get shakeX(): number { return this.tremorOn ? this.tremor.offsetX : 0; }
  get shakeY(): number { return this.tremorOn ? this.tremor.offsetY : 0; }

  /**
   * The blocked mask of the running world. It rebuilds the ridge list — the
   * mask is the contract, and ridge *rendering* is somebody else's file.
   */
  setBlocked(mask: Uint8Array | null): void {
    this.ridges = cellsOf(mask, BLOCK_RIDGE);
  }

  /**
   * The `silt`, `tremor` and `siltn` knobs (`fast/knobs.ts`).
   *
   * `siltn` multiplies the grain cap. A smaller cap drops the grains over
   * it at once, because a burst that already spawned them must not keep a
   * crowd the knob has just refused.
   */
  configure(silt: boolean, tremor: boolean, siltn: number): void {
    this.siltOn = silt;
    this.tremorOn = tremor;
    const cap = Math.max(0, Math.round(SILT_MAX * siltn));
    if (cap === this.cap) return;
    this.cap = cap;
    if (this.pool.length < cap * GRAIN_STRIDE) this.pool = new Float32Array(cap * GRAIN_STRIDE);
    if (this.grains > cap) this.grains = cap;
  }

  /**
   * What the frame knows about the arc. The caller runs it before `update`.
   *
   * @param stage `Scoreboard.quakeStage`, passed straight through.
   * @param settleT the share of the drain of the settlement that has
   * passed, 0..1, from the settlement clock (R2093), or null before the
   * clock arrives.
   * @param emitSilt false while the fluff switch is off (R124): the tremor
   * clock still runs — the camera feed reads `shakeX`/`shakeY` either way —
   * but no grains spawn, and the live ones recycle at once rather than
   * falling behind a layer nobody draws (§10.3).
   */
  feed(stage: number, settleT: number | null, emitSilt: boolean): void {
    this.stage = stage;
    this.settleT = settleT;
    this.emitSilt = emitSilt;
  }

  /**
   * Starts one burst of `stage` now, and sheds its silt on the next frame
   * (R1535). The reel page calls it at the second its scene names. The
   * burst rings down only while `feed` names a stage over 0.
   */
  shock(stage: 1 | 2 | 3): void {
    this.owedSilt += this.tremor.shock(stage);
  }

  update(frame: FrameInfo): void {
    const dt = frame.dtMs / 1000;
    const silt = this.tremor.step(dt, this.stage, this.settleT) + this.owedSilt;
    this.owedSilt = 0;
    const q = this.quads;
    if (!this.emitSilt || !this.siltOn) {
      this.grains = 0;
      q.begin();
      return;
    }
    if (silt > 0) this.shakeSiltLoose(silt, frame.view);
    this.stepSilt(dt);
    this.pack(frame.cull);
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
  }

  /**
   * The layer draws nothing while it is invisible, and it still steps the
   * clock — but only because the engine never hides it. See the header.
   */
  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** How many grains are in the air. The tests read it. */
  get count(): number { return this.grains; }

  /** The live grain cap, which the `siltn` knob moves. The tests read it. */
  get siltCap(): number { return this.cap; }

  /**
   * Picks ridge cells inside the view and drops a grain off each.
   *
   * The list is rejection-sampled rather than scanned: with the camera on
   * open water there simply is less silt, which is correct
   * (`render/quake.ts:109-131`).
   */
  private shakeSiltLoose(count: number, view: ViewRect): void {
    if (this.ridges.length === 0) return;
    const p = this.pool;
    let made = 0;
    for (let tries = 0; tries < count * 4 && made < count; tries++) {
      if (this.grains >= this.cap) return;
      const cell = this.ridges[(this.rand() * this.ridges.length) | 0];
      // The two halves of the centre, so a try allocates no tuple.
      const cx = cellCenterX(cell), cy = cellCenterY(cell);
      if (cx < view.x0 || cx > view.x1 || cy < view.y0 || cy > view.y1) continue;
      made++;
      const at = this.grains * GRAIN_STRIDE;
      this.grains++;
      p[at] = cx + (this.rand() - 0.5) * HEX_SIZE * 1.4;
      p[at + 1] = cy + (this.rand() - 0.5) * HEX_SIZE * 1.4;
      // The cell is the 16 px radial dot the PixiJS layer draws into its
      // texture, so the sprite scale is the number it sets there.
      p[at + 5] = (0.5 + this.rand() * 0.8) * SILT_SIZE_PX / this.cell.w;
      const vy = SILT_FALL_PX_PER_SEC * (1 + this.rand() * SILT_FALL_SPAN_FRAC);
      p[at + 2] = (this.rand() - 0.5) * 2 * vy * SILT_DRIFT_FRAC;
      p[at + 3] = vy;
      p[at + 4] = 0;
    }
  }

  /** Drifts, falls and retires the grains, keeping the pool order. */
  private stepSilt(dt: number): void {
    const p = this.pool;
    let live = 0;
    for (let i = 0; i < this.grains; i++) {
      const at = i * GRAIN_STRIDE;
      const age = p[at + 4] + dt;
      if (age >= SILT_LIFE_SEC) continue;
      const to = live * GRAIN_STRIDE;
      p[to] = p[at] + p[at + 2] * dt;
      p[to + 1] = p[at + 1] + p[at + 3] * dt;
      p[to + 2] = p[at + 2];
      p[to + 3] = p[at + 3];
      p[to + 4] = age;
      p[to + 5] = p[at + 5];
      live++;
    }
    this.grains = live;
  }

  /**
   * One `silt` cell for each grain on screen. Puff out, settle out: the
   * grain appears as the rock lets go and thins away as it falls
   * (`render/quake.ts:154-157`).
   */
  private pack(cull: ViewRect): void {
    const q = this.quads;
    const p = this.pool;
    q.begin();
    for (let i = 0; i < this.grains; i++) {
      const at = i * GRAIN_STRIDE;
      const x = p[at];
      const y = p[at + 1];
      if (outside(x, y, cull.x0, cull.y0, cull.x1, cull.y1)) continue;
      const t = p[at + 4] / SILT_LIFE_SEC;
      const alpha = SILT_ALPHA * (t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85);
      q.sprite(x, y, 0, this.cell, SILT_COLOR, alpha, p[at + 5]);
    }
  }
}
